var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import fieldLabelStyles from '@cof/fs-field-label-core/lib/field-label.lit.min.js';
import { FormControlMixin } from '@cof/fs-form-control';
import formFieldStyles from '@cof/fs-form-field-core/lib/form-field.lit.min.js';
import helperTextStyles from '@cof/fs-helper-text-core/lib/helper-text.lit.min.js';
import selectStyles from '@cof/fs-select-core/lib/select.lit.min.js';
import { css, html, LitElement } from 'lit';
import { property, query, state } from 'lit/decorators.js';
const eventForwarder = (target) => (event) => {
    event.stopPropagation();
    const e = new Event(event.type, event);
    target.dispatchEvent(e);
};
/**
 * MutationObserver likely doesn't exist in a server context so we need to ensure it exists before
 * attempting to create a new MutationObserver so this code can be isomorphic
 */
const mutationObserverExists = typeof MutationObserver !== 'undefined';
const mutationObserverMock = {
    observe() {
        return;
    },
    disconnect() {
        return;
    }
};
/**
 * `FsSelect` implements the Financial Services extension of the Gravity Design System's select component.
 *
 * Be sure to consult with the design guidelines for the select component to ensure proper state usage, it is possible to
 * force the select component into an unsupported state.
 *
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-select-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `<fs-select>` call
 *
 * ```javascript
 * import '@cof/fs-select-wc/fs-select.js';
 * ```
 *
 * To import the `FsSelect` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsSelect } from '@cof/fs-select-wc';
 * ```
 *
 * ## Anatomy
 * ### DOM Structure
 *
 * ```html
 * <fs-select
 *   ?compact=${compact}
 *   ?disabled=${disabled}
 *   error-text="${errorText}"
 *   helper-text="${helperText}"
 *   ?required=${required}
 * >
 *   <span slot="label">${label}</span>
 *   <fs-icon
 *     description="Icon"
 *     icon="${compact ? `fs-16--placeholder` : `fs-24--placeholder`}"
 *     slot="icon"
 *   ></fs-icon>
 *   <option value="">Please select</option></option>
 *   <option value="lorem ipsum">Lorem ipsum</option>
 *   <option value="dolor sit amet">Dolor sit amet</option>
 *   <option value="consectetur">Consectetur</option>
 *   <option value="adipiscing">Adipiscing</option>
 *   <option value="elit">Elit</option>
 * </fs-select>
 * ```
 *
 * @element fs-select
 * @slot - \<option\> elements to serve as the optional values that can be selected via the select input.
 * @slot icon - Icon to be displayed leading the select input.
 * @slot label - Text to be displayed inside the label.
 * @emits change - Triggered when the \<select\> value is changed.
 */
export class FsSelect extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        /**
         * Whether or not the input is compact.
         * @attr
         */
        this.compact = false;
        /**
         * Whether or not the input is disabled.
         * @attr
         */
        this.disabled = false;
        /**
         * Error text to be displayed if the selected item is invalid.
         * @attr error-text
         */
        this.errorText = '';
        /**
         * Helper text to be displayed to describe the input in addition to the label.
         * This will not be displayed if errorText is also present.
         * @attr helper-text
         */
        this.helperText = '';
        /**
         * The name of the input for use in a wrapping form.
         * @attr
         */
        this.name = 'fs-select';
        /**
         * Whether or not the input is required.
         * @attr
         */
        this.required = false;
        /**
         * Internal state to track the required state of the input for display purposes.
         *
         * @private
         */
        this._required = true;
        /**
         * Enables native event propagation through the web component.
         *
         * @private
         */
        this._forwardEvent = eventForwarder(this);
        /**
         * Observe changes to the \<option\>s made available to the host.
         *
         * @private
         */
        this._optionsMutationObserver = mutationObserverExists
            ? new MutationObserver((mutations) => {
                mutations.forEach((mutation) => this._reconcileOptions(mutation));
            })
            : mutationObserverMock;
        /**
         * Observe changes on individual \<option\>s.
         *
         * @private
         */
        this._optionMutationObserver = mutationObserverExists
            ? new MutationObserver((mutations) => {
                mutations.forEach((mutation) => this._reconcileOption(mutation));
            })
            : mutationObserverMock;
        /**
         * Map of internal \<option\> "clones" to their light DOM equivalents.
         *
         * @private
         */
        this._optionToCloneMap = new WeakMap();
        /**
         * Internal value.
         *
         * @private
         */
        this._value = null;
        /**
         * Handle any change event, and republish the event.
         *
         * @param event - The event triggering the change.
         */
        this._handleChange = (event) => {
            this.value = event.target.value;
            this._forwardEvent(event);
            return false;
        };
    }
    static get styles() {
        return [
            css `
        :host {
          display: block;
        }
      `,
            selectStyles,
            fieldLabelStyles,
            formFieldStyles,
            helperTextStyles
        ];
    }
    /**
     * The value of the slider.
     * @attr
     */
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
        this.reconcileValidity();
        this.reconcileValue(value);
        this._internals.setFormValue(value.toString());
    }
    /**
     * Called when the component is first updated, this will link the underlying form control to a wrapping form.
     *
     * @param changedProperties - The properties that were changed when the update occurred.
     * @protected
     */
    firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        this.linkControl(this.shadowRoot.querySelector('select'));
        /**
         * Gets each option that is not inside an optgroup, and each optgroup.
         * We are using the :not selector so we don't pull duplicate options since options under an optgroup will
         * automatically be added with its optgroup
         */
        const optionsAndOptionGroups = Array.from(this.querySelectorAll('option:not(optgroup option), optgroup'));
        optionsAndOptionGroups.forEach((element) => {
            if (element instanceof HTMLOptGroupElement) {
                this.addGroup(element);
            }
            else if (element instanceof HTMLOptionElement) {
                this.add(element);
            }
        });
        this._optionsMutationObserver.observe(this, { childList: true, subtree: true });
    }
    /**
     * Clone and add an \<option\> to the \<select\>.
     *
     * @param option The \<option\> to be added.
     */
    add(option) {
        const optionCopy = this._clone(option);
        this.input.add(optionCopy);
        this.reconcileValidity();
    }
    /**
     * Clone and add an \<optgroup\> to the \<select\>.
     *
     * @param optionGroup The \<optgroup\> to be added.
     */
    addGroup(optionGroup) {
        const optionGroupCopy = optionGroup.cloneNode(false);
        // Clone over \<optgroup\> child \<option\>s.
        optionGroup
            .querySelectorAll('option')
            .forEach((option) => optionGroupCopy.appendChild(this._clone(option)));
        this.input.add(optionGroupCopy);
        this.reconcileValidity();
    }
    /**
     * Called when the element is being disconnected.
     * Remove mutation observers.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        this._optionsMutationObserver.disconnect();
        this._optionMutationObserver.disconnect();
    }
    formResetCallback() {
        this.value = '';
    }
    /**
     * Remove the \<option\> at the specified index from the \<select\>.
     * If no index is provided, call the native "remove" method.
     *
     * @param index The index of the \<option\> to be removed from the \<select\>.
     */
    remove(index) {
        if (index) {
            this.input.remove(index);
        }
        else {
            super.remove();
        }
    }
    /**
     * Render the component.
     *
     * @returns TemplateResult
     */
    render() {
        return html `
      <div class="fs-form-field ${this.compact ? 'fs-form-field--compact' : ''}">
        <label
          class="
            fs-field-label
            ${this.compact ? 'fs-field-label--compact' : ''}
            ${this.disabled ? 'fs-field-label--disabled' : ''}
          "
          for="select"
        >
          <slot name="label"></slot>
          ${this.required ? '' : html `<span class="fs-field-label__optional">(optional)</span>`}
        </label>

      <div
        class="
          fs-select
          ${this.compact ? 'fs-select--compact' : ''}
          ${this.disabled ? 'fs-select--disabled' : ''}
          ${this.showError || this.errorText ? 'fs-select--error' : ''}
        "
      >
        <slot name="icon"></slot>
        <select
          @change=${this._handleChange}
          id="select"
          @input=${this._handleChange}
          aria-describedby="helper"
          ?disabled=${this.disabled}
          ?required=${this.required}
          name="${this.name}"
        >
        </select>
      </div>

      ${this.helperText != null && this.helperText !== '' && this.showError === false && !this.errorText
            ? html `
            <span
              class="
                fs-helper-text
                ${this.disabled ? 'fs-helper-text--disabled' : ''}"
                id="helper"
            >
              ${this.helperText}
            </span>
          `
            : ''}
      ${this.showError || this.errorText
            ? html `<span class="fs-helper-text fs-helper-text--error" id="helper">${this.errorText || this.validationMessage}</span>`
            : ''}
    </div>
    `;
    }
    /**
     * Clone an \<option\> for internal use.
     *
     * @param option The \<option\> to be cloned.
     * @return HTMLOptionElement The clone of the \<option\> element.
     */
    _clone(option) {
        const optionCopy = option.cloneNode(true);
        option.selected = option.value === this.value;
        // cloned Node does not have the original node's selection state
        optionCopy.selected = option.selected || option.hasAttribute('selected');
        optionCopy.label = option.label;
        this._optionMutationObserver.observe(option, {
            attributes: true,
            attributeFilter: ['value', 'label'],
            characterData: true
        });
        this._optionToCloneMap.set(option, optionCopy);
        if (optionCopy.selected) {
            this.value = optionCopy.value;
        }
        return optionCopy;
    }
    /**
     * Reconcile any changes to the light DOM \<option\>s with their internal representations.
     *
     * @param mutation The mutation record that triggered the reconciliation.
     * @private
     */
    _reconcileOptions(mutation) {
        const { addedNodes, removedNodes } = mutation;
        if (addedNodes.length) {
            Array.from(addedNodes).forEach((node) => {
                if (node.nodeName === 'OPTION') {
                    this.add(node);
                }
                else if (node.nodeName === 'OPT_GROUP') {
                    this.addGroup(node);
                }
            });
        }
        if (removedNodes.length) {
            Array.from(removedNodes).forEach((node) => {
                // Remove relevant nodes.
                if (node.localName === 'option') {
                    let index;
                    for (let i = 0; i < this.input.options.length; i += 1) {
                        if (this.input.options[i].value === node.value) {
                            index = i;
                            break;
                        }
                    }
                    this.remove(index);
                }
                else if (node.localName === 'optgroup') {
                    const { label } = node;
                    this.input.querySelector(`[label="${label}"]`).remove();
                }
            });
            // Clear the value if the selected option was removed.
            if (this.value) {
                const selected = this.input.querySelector(`[value="${this.value}"]`);
                if (!selected) {
                    this.value = '';
                    this._internals.setFormValue('');
                }
            }
        }
        // This is processed on the next tick to ensure that underlying browser validity checks have completed before we recheck.
        // This may be unnecessary in future versions of the FormControlMixin.
        // TODO: Re-eveluate the necessity of this.
        setTimeout(() => {
            this.reconcileValidity();
        });
    }
    /**
     * Reconcile any changes to the light DOM \<option\> with its internal representation.
     *
     * @param mutation The mutation record that triggered the reconciliation.
     * @private
     */
    _reconcileOption(mutation) {
        const target = mutation.target;
        const clone = this._optionToCloneMap.get(target);
        clone.value = target.value;
        clone.innerText = target.innerText;
        clone.label = target.label;
        clone.selected = clone.value === this.value || target.selected;
    }
}
/** The element's canonical name for use with safe define */
FsSelect.canonicalName = 'select';
__decorate([
    property({ type: Boolean, reflect: true })
], FsSelect.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsSelect.prototype, "disabled", void 0);
__decorate([
    property({ type: String, attribute: 'error-text' })
], FsSelect.prototype, "errorText", void 0);
__decorate([
    property({ type: String, attribute: 'helper-text' })
], FsSelect.prototype, "helperText", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsSelect.prototype, "name", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsSelect.prototype, "required", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsSelect.prototype, "value", null);
__decorate([
    query('select')
], FsSelect.prototype, "input", void 0);
__decorate([
    query('slot:not([name])')
], FsSelect.prototype, "slotElement", void 0);
__decorate([
    state()
], FsSelect.prototype, "_required", void 0);
//# sourceMappingURL=Select.js.map